import { Box, Grid, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  ContractOverviewCompensationsDocument,
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageContractVersionCompensationFragment,
  ContractVersionsPageEmployeeFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { CurrencyContextProvider } from 'modules/compensations/lib/CurrencyContext'
import { isFeatureEnabled } from 'modules/compensations/lib/utils'
import ContractData from 'modules/contracts/components/ContractDetails/Section/ContractData'
import Header from 'modules/contracts/components/ContractDetails/Section/Header'
import { CycleInfo } from 'modules/contracts/components/CycleInfo'
import { AdditionalCompensationsList } from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationsList/AdditionalCompensationsList'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'

import AdditionalCompensationsDeprecated from './AdditionalCompensations'
import LoadingState from './LoadingState'
import Salary from './Salary'

interface Props {
  contractVersion: ContractVersionPageContractVersionFragment
  employee: ContractVersionsPageEmployeeFragment
  compensations: ContractVersionsPageContractVersionCompensationFragment[]
}

const Compensation = ({ contractVersion, employee, compensations }: Props) => {
  const { company } = useCurrentSession()
  const {
    hasCompensations: hasNewCompensationsManagementEnabled,
    loading: hasCompensationsLoading,
  } = useHasCompensations()
  const { data: contractsCompensations, loading } = useQuery(
    ContractOverviewCompensationsDocument,
    {
      variables: {
        companyId: company.id,
        contractVersionId: contractVersion.id ?? 0,
        employeeId: employee.id,
      },
    }
  )
  const canEdit = contractVersion._policies?.edit?.can ?? false
  const canSeeAdditionalCompensations =
    contractVersion._policies?.seeAdditionalCompensation?.can ?? false
  const canSeeGrossSalary = contractVersion._policies?.seeGrossSalary?.can ?? false
  const canSeePayrollPolicy = contractVersion._policies?.seePayrollPolicy?.can ?? false

  if (
    !canSeeAdditionalCompensations &&
    !canSeeGrossSalary &&
    !canSeePayrollPolicy
  ) {
    return null
  }

  const isCompensationsEmpty = compensations.length === 0

  const showLegacyAdditionalCompensations =
    !hasNewCompensationsManagementEnabled &&
    canSeeAdditionalCompensations &&
    compensations.length > 0 &&
    (!isCompensationsEmpty || canEdit)

  if (loading || hasCompensationsLoading) {
    return <LoadingState />
  }

  const newAdditionalCompensations = hasNewCompensationsManagementEnabled
    ? (contractsCompensations?.contracts.additionalCompensationsConnection.nodes ?? [])
    : []

  const cycleInfo = contractsCompensations?.compensations.cyclesByEmployeesConnection.nodes[0]
  const showNewAdditionalCompensations =
    hasNewCompensationsManagementEnabled &&
    canSeeAdditionalCompensations &&
    newAdditionalCompensations.length > 0

  const showAdditionalCompensations =
    showLegacyAdditionalCompensations || showNewAdditionalCompensations
  const contractPayrollPolicy = contractsCompensations?.contracts.contractVersion?.payrollPolicy
  const hasPayrollBundleFeature =
    contractsCompensations?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.payroll_management
    )?.available ?? false

  const isCyclesManagementFeatureEnabled = isFeatureEnabled(
    'dev_compensations_cycles_management',
    contractsCompensations?.features.featuresConnection
  )

  return (
    <Box gap="s16" flexDirection="column">
      <Header icon="payroll" title={i18n.t('contract_versions.compensation.title')} />
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 12, xs: 12, s: showAdditionalCompensations ? 6 : 12 }} gap="s8">
          <Salary
            legalEntityId={employee.legalEntity?.id}
            contractVersion={contractVersion}
            isNewAdditionalCompensationsSupportEnabled={hasNewCompensationsManagementEnabled}
            contractPayrollPolicy={contractPayrollPolicy}
            hasPayrollBundleFeature={hasPayrollBundleFeature}
            cycleInfo={
              hasNewCompensationsManagementEnabled &&
              isCyclesManagementFeatureEnabled &&
              cycleInfo ? (
                <>
                  <Text size="200" weight="normal" color="grey800">
                    {i18n.t('contract_versions.compensation.payrollCycle')}
                  </Text>
                  <CycleInfo
                    countryLabel={cycleInfo.countryLabel}
                    recurrence={cycleInfo.recurrence}
                  />
                </>
              ) : null
            }
          />
        </Box>
        {showAdditionalCompensations && (
          <Box gridColSpan={{ xxs: 12, xs: 12, s: 6 }} gap="s8">
            {showNewAdditionalCompensations && (
              <ContractData>
                <Box gap="s24">
                  <Text size="200" weight="normal" color="grey800">
                    {i18n.t('contract_versions.compensation.additional_compensations.title')}
                  </Text>
                  <CurrencyContextProvider
                    defaultCurrency={isCyclesManagementFeatureEnabled ? cycleInfo?.currency : null}
                  >
                    <AdditionalCompensationsList
                      additionalCompensations={newAdditionalCompensations ?? []}
                    />
                  </CurrencyContextProvider>
                </Box>
              </ContractData>
            )}
            {showLegacyAdditionalCompensations && employee.legalEntity?.currency && (
              <AdditionalCompensationsDeprecated
                compensations={compensations}
                legalEntityCurrency={employee?.legalEntity?.currency}
              />
            )}
          </Box>
        )}
      </Grid>
    </Box>
  )
}

export default Compensation
